import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Copy,
  Eye as EyeIcon,
  EyeOff,
  Flag,
  History,
  Map as MapIcon,
  MessagesSquare,
  MoreHorizontal,
  Share,
  ShieldBan,
  ThumbsDown,
  ThumbsUp,
  UserX,
  VolumeX,
} from 'lucide-react';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { CommunityReportsBanner } from '../../../components/Post/CommunityReportsBanner';
import ModerationBanner, {
  type ModerationMeta,
} from '../../../components/Post/ModerationBanner';
import { ReaderModerationBanner } from '../../../components/Post/ReaderModerationBanner';
import UserPostComponent, {
  type UserPostComponentHandle,
} from '../../../components/Post/UserPost.component';
import ReportPostDialog from '../../../components/ReportPost/ReportPostDialog';
import ShareStoryModal from '../../../components/ShareStory/ShareStoryModal';
import {
  BookmarkIcon,
  CommentIcon,
  HeartIcon,
} from '../../../components/UserFeed/FeedIcons';
import { PRIVATE_URIS } from '../../../constants/privateURIs.constants';
import { useAuth } from '../../../context/AuthContext';
import { useSocket } from '../../../context/WebsocketContext';
import { HIDE_POST_FROM_FEED } from '../../../graphql/resolvers/feed/feedMutations/hidePostFromFeed.gql';
import { SUBMIT_FEEDBACK_SIGNAL } from '../../../graphql/resolvers/feed/feedMutations/submitFeedbackSignal.gql';
import { TOGGLE_ACCOUNT_BLOCK } from '../../../graphql/resolvers/feed/feedMutations/toggleAccountBlock.gql';
import { TOGGLE_AUTHOR_MUTE } from '../../../graphql/resolvers/feed/feedMutations/toggleAuthorMute.gql';
import { TOGGLE_THREAD_MUTE } from '../../../graphql/resolvers/feed/feedMutations/toggleThreadMute.gql';
import { GET_POST_BY_ID_QUERY } from '../../../graphql/resolvers/postQueries/getPostById.gql';
import { RECORD_POST_READ_MUTATION } from '../../../graphql/resolvers/postMutations/recordPostRead.gql';
import { postAnalysisPath } from '../../../helpers/postAnalysisPath';
import { postHistoryPath } from '../../../helpers/postHistoryPath';
import { postSentimentPath } from '../../../helpers/postSentimentPath';
import { resolveNavRoute } from '../../../helpers/resolveNavRoute';
import { useFeedPostSubscriptions } from '../../../hooks/useFeedPostSubscriptions';
import { usePostInteractions } from '../../../hooks/usePostInteractions';
import { usePostReconnectReconcile } from '../../../hooks/usePostReconnectReconcile';
import { useReplyDrafts } from '../../../hooks/useReplyDrafts';
import type { FeedPost } from '../../../interfaces/feed.interface';
import './post.scss';

const UserPost = () => {
  const params = useParams();
  const { postId } = params;
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { authenticated, onEvent } = useSocket();
  const { getDraftForPost } = useReplyDrafts(user?.id);

  // TRIL-173 SP2a: the Apollo cache is the single source of truth for the
  // current post. Reads come from `post = data?.getPostById`; edits
  // (updatePost/saveRewrite/republish), like/bookmark toggles, live score
  // updates, and the moderation WS handlers all write `Post:{id}` in the
  // cache, so this query re-renders the page automatically. The previous
  // `userFeedPost` synth assembled from postsStore/feedStore/postStore (and
  // its SP1 transitional casts) is gone.
  const { data, refetch } = useQuery(GET_POST_BY_ID_QUERY, {
    variables: { postId: postId ?? '' },
    skip: !postId,
    fetchPolicy: 'cache-and-network',
  });
  const post = data?.getPostById;

  const postReplyDraft = postId ? getDraftForPost(postId) : undefined;
  const [isShareOpen, setIsShareOpen] = useState(false);

  // TRIL-187 — username-prefixed feed route for redirect-after-removal.
  // PRIVATE_URIS.FEED is the template 'user/:username/feed'; resolveNavRoute
  // substitutes the current username and ensures a leading slash. Falls back
  // to empty (handler then skips the redirect) if username is unavailable —
  // shouldn't happen on this auth-gated page, but defensible.
  const feedRoute = user?.username
    ? resolveNavRoute(PRIVATE_URIS.FEED, user.username)
    : '';

  // TRIL-187 — evict the cached feed query before redirecting so the muted/
  // hidden/blocked post falls out of the cache-rendered feed on next mount.
  // Owning the eviction at the SOURCE of the action (this handler) decouples
  // us from any cross-component history-tracking mechanism on the feed side,
  // which is route-scoped (mounts fresh on arrival, can't observe "we came
  // from /post/X"). Without this evict, the feed's merge policy reconciles
  // the next fetch and keeps the just-removed post visible. (TRIL-189 owns
  // the broader fix for the recommendationFeed prevPathRef mechanism.)
  const apolloClient = useApolloClient();
  const redirectToFeed = React.useCallback(() => {
    if (!feedRoute) return;
    apolloClient.cache.evict({ fieldName: 'getUserFeed' });
    apolloClient.cache.gc();
    navigate(feedRoute);
  }, [apolloClient, feedRoute, navigate]);

  // TRIL-54 — Ellipsis menu mutations + state
  const [hidePostFromFeedMutation] = useMutation(HIDE_POST_FROM_FEED);
  const [toggleThreadMuteMutation] = useMutation(TOGGLE_THREAD_MUTE);
  const [toggleAccountBlockMutation] = useMutation(TOGGLE_ACCOUNT_BLOCK);
  const [toggleAuthorMuteMutation] = useMutation(TOGGLE_AUTHOR_MUTE);
  const [submitFeedbackSignalMutation] = useMutation(SUBMIT_FEEDBACK_SIGNAL);
  const [recordPostRead] = useMutation(RECORD_POST_READ_MUTATION);
  const readFiredRef = useRef<string | null>(null);
  const [ownRead, setOwnRead] = useState<{ postId: string; reach: number } | null>(
    null,
  );
  const [actionToast, setActionToast] = useState<{
    message: string;
    variant: 'success' | 'error';
  } | null>(null);
  const actionToastTimeoutRef = useRef<number | null>(null);
  const [reportDialog, setReportDialog] = useState<{
    open: boolean;
    postId: string;
  }>({ open: false, postId: '' });

  const showActionToast = React.useCallback(
    (message: string, variant: 'success' | 'error' = 'success') => {
      setActionToast({ message, variant });
      if (actionToastTimeoutRef.current)
        clearTimeout(actionToastTimeoutRef.current);
      actionToastTimeoutRef.current = window.setTimeout(() => {
        setActionToast(null);
        actionToastTimeoutRef.current = null;
      }, 2600);
    },
    [],
  );

  // Derived display values read straight off the cached post.
  const authorUsername = post?.postAnalytics?.author?.username || '';

  const authorId =
    post?.authorId ?? post?.postAnalytics?.author?.userId ?? null;
  const isOwnPost =
    (!!authorId && authorId === user?.id) || authorUsername === user?.username;

  const liveLikeCount = post?.viewerInteractions?.likeCount ?? 0;
  const liveBookmarkCount = post?.viewerInteractions?.bookmarkCount ?? 0;
  // Prefer this viewer's own just-counted read (returned by the mutation) over
  // the page-load value, so the eye ticks up immediately without a refresh —
  // but only for the post it was counted on.
  const liveReachCount =
    (ownRead && ownRead.postId === postId ? ownRead.reach : null) ??
    post?.viewerInteractions?.reachCount ??
    0;

  // TRIL-332: record a unique read once the post is loaded. The server dedups
  // per viewer (anonymous / author-self / repeat reads return counted=false), so
  // we fire once per postId (ref-guarded against re-renders / strict-mode
  // double-invoke), then reflect our own first read in the eye count live.
  useEffect(() => {
    if (!postId || !post || readFiredRef.current === postId) return;
    readFiredRef.current = postId;
    recordPostRead({ variables: { input: { postId } } })
      .then((res) => {
        const r = res.data?.recordPostRead;
        if (r?.counted && typeof r.reachCount === 'number') {
          setOwnRead({ postId, reach: r.reachCount });
        }
      })
      .catch(() => {});
  }, [postId, post, recordPostRead]);
  const liveHasLiked = post?.viewerInteractions?.hasLiked ?? false;
  const liveHasBookmarked = post?.viewerInteractions?.hasBookmarked ?? false;

  // Score — server now guarantees post.score is correct (TRIL-102).
  // Read directly; no storyAnalysis fallback needed.
  const displayScore: number | null =
    typeof post?.score === 'number' ? post.score : null;
  const displayReplyCount = post?.replyCount ?? 0;

  // The cached post is the live post for toggleLike/toggleBookmark and the
  // UserPostComponent. Those consumers' types require FeedPost; the cache
  // post is a superset for the fields they read (id, viewerInteractions,
  // score, textBlocks, replyCount, storyAnalysis), so cast at the boundary.
  // `author`, `feedMetadata`, `postId`, and `title` are not on the cached
  // shape, so synthesize them here (reproducing the old synth's hardcoded
  // feedMetadata.entryType) for the component that reads them.
  const livePostForToggle: FeedPost | undefined = post
    ? ({
        ...post,
        postId: post.id,
        feedMetadata: { entryType: 'AUTHORED' },
        author: {
          username: post.postAnalytics?.author?.username || '',
          userId: post.postAnalytics?.author?.userId || post.authorId || '',
          authorId: post.authorId || post.postAnalytics?.author?.userId || '',
          avatarUrl: '',
        },
        title: post.textBlocks?.[0]?.data?.header || '',
      } as unknown as FeedPost)
    : undefined;
  const repliesDrawerRef = useRef<UserPostComponentHandle>(null);

  const { toggleLike, toggleBookmark } = usePostInteractions();

  // Shim so the WS / reconcile / refresh call sites keep their old
  // `fetchPostById(...)` shape while the data source is now useQuery.
  // refetch() goes to the network and writes the result into the cache,
  // which re-renders this page via the useQuery above.
  const fetchPostById = React.useCallback(() => {
    if (!postId) return;
    void refetch();
  }, [refetch, postId]);

  // Refetch on WS reconnect + replay into Redux (feed cards). Kept in its
  // own hook so the false -> true transition logic can be tested with RTL
  // renderHook rather than rendering the whole post page. After TRIL-191
  // the refetch alone updates the Apollo cache (every consumer subscribes
  // via useQuery); the `fetchedPostData` arg is retained as a pass-through
  // for the hook's test harness (no-Redux-dispatch assertion).
  usePostReconnectReconcile({
    postId,
    authenticated,
    fetchPostById,
    fetchedPostData: data,
  });

  // Register the connection as a PostWatchers entry for this post so
  // server-side broadcasts (post_status_changed, INTERACTION_UPDATED,
  // new_replies) actually reach the page. Reuses the feed-page hook
  // for set-diff + reconnect handling.
  useFeedPostSubscriptions(postId ? [postId] : []);

  // After EditRewrite saves, it navigates back here with
  // location.state.refreshAfterSave set. Same-URL navigation doesn't
  // remount this component, so we refetch to pull fresh server state into
  // the cache; the useQuery above then re-renders with the new post.
  const lastRefreshSignalRef = useRef<number | null>(null);
  useEffect(() => {
    const signal = (location.state as { refreshAfterSave?: number } | null)
      ?.refreshAfterSave;
    if (!signal || !postId) return;
    if (lastRefreshSignalRef.current === signal) return;
    lastRefreshSignalRef.current = signal;
    fetchPostById();
  }, [location.state, postId, fetchPostById]);

  // Subscribe to community_reports_updated WS events — refetch the post
  // when the event matches the current postId so communityReports data
  // stays current without a full page reload. (This event is not handled
  // by the moderation WS dispatcher, so this is its only cache-refresh
  // path.)
  useEffect(() => {
    const off = onEvent(
      'community_reports_updated',
      (payload: { postId: string }) => {
        if (payload?.postId === postId) {
          fetchPostById();
        }
      },
    );
    return off;
  }, [onEvent, postId, fetchPostById]);

  return (
    post && (
      <>
        <div className="POST">
          <div className="POST__ACTIONS__CONTAINER">
            <div className="POST__ACTIONS_COL">
              {displayScore !== null && (
                <Link
                  to={postAnalysisPath(postId ?? '')}
                  className="post-score"
                  title="View full story analysis"
                >
                  SQS: {displayScore.toFixed(1)}
                </Link>
              )}
              <>
                <button
                  type="button"
                  className={`post-action ${liveHasLiked ? 'post-action--active-like' : ''}`}
                  onClick={() =>
                    livePostForToggle && toggleLike(livePostForToggle)
                  }
                  aria-label="Like"
                >
                  <HeartIcon
                    size={18}
                    fill={liveHasLiked ? '#e0245e' : 'currentColor'}
                  />
                  <span>{liveLikeCount}</span>
                </button>
                <button
                  type="button"
                  className={`post-action ${liveHasBookmarked ? 'post-action--active-bookmark' : ''}`}
                  onClick={() =>
                    livePostForToggle && toggleBookmark(livePostForToggle)
                  }
                  aria-label="Bookmark"
                >
                  <BookmarkIcon
                    size={18}
                    fill={liveHasBookmarked ? '#1d9bf0' : 'currentColor'}
                  />
                  <span>{liveBookmarkCount}</span>
                </button>
                <button
                  type="button"
                  className="post-action"
                  aria-label="Reply"
                  onClick={() => repliesDrawerRef.current?.openReplyModal()}
                >
                  <CommentIcon size={18} fill="currentColor" />
                  <span>{displayReplyCount}</span>
                </button>
                <div
                  className="post-action post-action--stat"
                  role="group"
                  aria-label="Reads"
                  title="Unique readers — people who opened this story"
                >
                  <EyeIcon size={18} />
                  <span>{liveReachCount}</span>
                </div>
                <Link
                  to={postSentimentPath(postId ?? '')}
                  className="post-action"
                  aria-label="Sentiment map"
                  title="Sentiment map — where this story lands"
                >
                  <MapIcon size={18} />
                </Link>
                <button
                  type="button"
                  className="post-action"
                  aria-label="View replies"
                  onClick={() => repliesDrawerRef.current?.openDrawer()}
                >
                  <MessagesSquare size={18} />
                </button>
                <button
                  type="button"
                  className="post-action"
                  aria-label="Share"
                  onClick={() => setIsShareOpen(true)}
                >
                  <Share size={18} />
                </button>

                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild>
                    <button
                      type="button"
                      className="post-action post-action--ellipsis"
                      aria-label="More actions"
                    >
                      <MoreHorizontal size={18} />
                    </button>
                  </DropdownMenu.Trigger>

                  <DropdownMenu.Portal>
                    <DropdownMenu.Content
                      className="feed-card__menu"
                      sideOffset={4}
                      align="end"
                    >
                      <DropdownMenu.Item
                        className="feed-card__menu-item"
                        onClick={async () => {
                          const url = `${window.location.origin}/post/${postId}`;
                          try {
                            if (navigator.clipboard?.writeText) {
                              await navigator.clipboard.writeText(url);
                              showActionToast('Link copied');
                            } else {
                              showActionToast('Clipboard unavailable', 'error');
                            }
                          } catch {
                            showActionToast('Could not copy link', 'error');
                          }
                        }}
                      >
                        <Copy size={16} />
                        <span>Copy link</span>
                      </DropdownMenu.Item>
                      <DropdownMenu.Item
                        className="feed-card__menu-item"
                        onClick={() => setIsShareOpen(true)}
                      >
                        <Share size={16} />
                        <span>Share post via...</span>
                      </DropdownMenu.Item>
                      {isOwnPost && (
                        <DropdownMenu.Item
                          className="feed-card__menu-item"
                          onClick={() =>
                            navigate(postHistoryPath(postId ?? ''))
                          }
                        >
                          <History size={16} />
                          <span>Version history</span>
                        </DropdownMenu.Item>
                      )}

                      <DropdownMenu.Separator className="feed-card__menu-separator" />

                      <DropdownMenu.Item
                        className="feed-card__menu-item"
                        onClick={async () => {
                          try {
                            await submitFeedbackSignalMutation({
                              variables: { postId, signal: 'MORE_LIKE' },
                            });
                            showActionToast("We'll show you more like this");
                          } catch {
                            showActionToast('Could not send feedback', 'error');
                          }
                        }}
                      >
                        <ThumbsUp size={16} />
                        <span>Show more like this</span>
                      </DropdownMenu.Item>
                      <DropdownMenu.Item
                        className="feed-card__menu-item"
                        onClick={async () => {
                          try {
                            await hidePostFromFeedMutation({
                              variables: { postId },
                            });
                            try {
                              await submitFeedbackSignalMutation({
                                variables: { postId, signal: 'LESS_LIKE' },
                              });
                              showActionToast("We'll show less like this");
                            } catch {
                              showActionToast(
                                'Hidden — feedback signal failed',
                                'error',
                              );
                            }
                          } catch {
                            showActionToast(
                              'Could not update preferences',
                              'error',
                            );
                          }
                        }}
                      >
                        <ThumbsDown size={16} />
                        <span>Show less like this</span>
                      </DropdownMenu.Item>

                      {!isOwnPost && (
                        <>
                          <DropdownMenu.Separator className="feed-card__menu-separator" />

                          <DropdownMenu.Item
                            className="feed-card__menu-item"
                            onClick={async () => {
                              const rootPostId = post?.rootPostId ?? postId;
                              try {
                                await toggleThreadMuteMutation({
                                  variables: { rootPostId },
                                });
                                showActionToast('Thread muted');
                                redirectToFeed();
                              } catch {
                                showActionToast(
                                  'Could not mute thread',
                                  'error',
                                );
                              }
                            }}
                          >
                            <VolumeX size={16} />
                            <span>Mute thread</span>
                          </DropdownMenu.Item>
                          <DropdownMenu.Item
                            className="feed-card__menu-item"
                            onClick={async () => {
                              try {
                                await hidePostFromFeedMutation({
                                  variables: { postId },
                                });
                                showActionToast('Post hidden from feed');
                                redirectToFeed();
                              } catch {
                                showActionToast('Could not hide post', 'error');
                              }
                            }}
                          >
                            <EyeOff size={16} />
                            <span>Hide post for me</span>
                          </DropdownMenu.Item>
                          <DropdownMenu.Item
                            className="feed-card__menu-item"
                            onClick={async () => {
                              if (!authorId) return;
                              try {
                                await toggleAuthorMuteMutation({
                                  variables: { authorId },
                                });
                                showActionToast('Account muted');
                                redirectToFeed();
                              } catch {
                                showActionToast(
                                  'Could not mute account',
                                  'error',
                                );
                              }
                            }}
                          >
                            <UserX size={16} />
                            <span>Mute account</span>
                          </DropdownMenu.Item>

                          <DropdownMenu.Separator className="feed-card__menu-separator" />

                          <DropdownMenu.Item
                            className="feed-card__menu-item feed-card__menu-item--danger"
                            onClick={async () => {
                              if (!authorId) return;
                              try {
                                await toggleAccountBlockMutation({
                                  variables: { accountId: authorId },
                                });
                                showActionToast('Account blocked');
                                redirectToFeed();
                              } catch {
                                showActionToast(
                                  'Could not block account',
                                  'error',
                                );
                              }
                            }}
                          >
                            <ShieldBan size={16} />
                            <span>Block account</span>
                          </DropdownMenu.Item>
                          <DropdownMenu.Item
                            className="feed-card__menu-item feed-card__menu-item--danger"
                            onClick={() =>
                              setReportDialog({
                                open: true,
                                postId: postId as string,
                              })
                            }
                          >
                            <Flag size={16} />
                            <span>Report post</span>
                          </DropdownMenu.Item>
                        </>
                      )}
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu.Root>
              </>
            </div>
          </div>
          {(() => {
            if (isOwnPost) {
              // Moderation comes straight from the cached post. The
              // moderation WS dispatcher (report_resolved / post_status_changed)
              // evicts+refetches or patches the cached Post, so this reflects
              // the live verdict — including the DISMISS case where moderation
              // is set back to null to clear the banner.
              const moderation = post.moderation;
              return moderation ? (
                <ModerationBanner
                  meta={moderation as ModerationMeta}
                  onEdit={
                    moderation.status === 'hidden_pending_rewrite'
                      ? () => navigate(`/post/${postId}/edit-rewrite`)
                      : undefined
                  }
                />
              ) : null;
            }
            // Always mount for non-authors — the component renders null when
            // initialStatus is PUBLISHED but its useEffect subscribes to
            // post_status_changed, so a live PUBLISHED → PENDING_REWRITE / REMOVED
            // transition still surfaces the banner.
            return (
              <ReaderModerationBanner
                key={post.id ?? ''}
                postId={post.id ?? ''}
                initialStatus={post.moderationStatus ?? 'PUBLISHED'}
                onSwapToRevised={() => fetchPostById()}
              />
            );
          })()}
          <CommunityReportsBanner
            communityReports={post.communityReports ?? null}
            isOwnPost={isOwnPost}
            postId={postId}
          />
          <div className="POST__CONTENT">
            <UserPostComponent
              ref={repliesDrawerRef}
              username={authorUsername}
              postReplyDraftId={postReplyDraft?.id}
              post={livePostForToggle as FeedPost}
              status={postReplyDraft?.status}
            />
          </div>
        </div>

        <ShareStoryModal
          open={isShareOpen}
          onClose={() => setIsShareOpen(false)}
          storyId={postId as string}
          storyTitle={
            post.textBlocks?.[0]?.type === 'Heading'
              ? (post.textBlocks[0].data?.header ?? 'Untitled')
              : 'Untitled'
          }
          storyUrl={`/post/${postId}`}
        />

        <ReportPostDialog
          open={reportDialog.open}
          onClose={() => setReportDialog({ open: false, postId: '' })}
          postId={reportDialog.postId}
          onReported={() => showActionToast('Report submitted')}
        />

        {actionToast && (
          <div
            className={`post-action-toast post-action-toast--${actionToast.variant}`}
            role={actionToast.variant === 'error' ? 'alert' : undefined}
          >
            {actionToast.message}
          </div>
        )}
      </>
    )
  );
};

export default UserPost;
